import { memo, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Boxes, ChevronRight, ExternalLink, LayoutDashboard, RefreshCw, Send, Sparkles, WalletCards, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppContext } from '@/context'
import Marquee from '@/components/Marquee'
import packageJson from '../../../../package.json'
import checkUpdate from '../checkUpdate'
import { getPlatform } from '@/utils/platform'

const Overlay = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  background: rgba(9, 19, 38, 0.56);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  animation: fadeIn 0.2s ease-out;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const Drawer = styled.aside`
  display: flex;
  flex-direction: column;
  width: min(86vw, 380px);
  min-height: 100%;
  overflow: hidden;
  color: #172033;
  background: #f7f9fc;
  box-shadow: 18px 0 48px rgba(7, 17, 36, 0.28);
  animation: slideIn 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
`

const DrawerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 20px 20px;
  border-bottom: 1px solid rgba(31, 199, 212, 0.16);
  background:
    radial-gradient(circle at 94% -30%, rgba(31, 199, 212, 0.24), transparent 52%),
    linear-gradient(135deg, #ffffff 0%, #effcfc 100%);
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`

const BrandMark = styled.div`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 1px solid rgba(31, 199, 212, 0.2);
  border-radius: 11px;
  color: #fff;
  background: linear-gradient(135deg, #1fc7d4, #119da9);
  box-shadow: 0 7px 16px rgba(31, 199, 212, 0.22);
`

const CloseButton = styled.button`
  display: grid;
  width: 36px;
  height: 36px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 10px;
  color: #168e98;
  background: rgba(31, 199, 212, 0.1);
  cursor: pointer;
  &:hover { background: rgba(31, 199, 212, 0.18); }
  &:active { transform: scale(0.94); }
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
`

const SectionTitle = styled.p`
  margin: 0 8px 10px;
  color: #8290a7;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.11em;
`

const MenuGroup = styled.div`
  overflow: hidden;
  margin-bottom: 22px;
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 47, 77, 0.05);
`

const MenuItem = styled.button<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 58px;
  padding: 0 14px;
  align-items: center;
  gap: 12px;
  border: 0;
  border-bottom: 1px solid #edf0f5;
  color: ${({ $active }) => ($active ? '#147f86' : '#26334a')};
  text-align: left;
  background: ${({ $active }) => ($active ? '#edfbfa' : '#fff')};
  cursor: pointer;
  &:last-child { border-bottom: 0; }
  &:active { background: #f1f6f8; }
`

const IconBox = styled.span<{ $active?: boolean }>`
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 10px;
  color: ${({ $active }) => ($active ? '#148e95' : '#52637d')};
  background: ${({ $active }) => ($active ? '#d6f5f2' : '#f1f4f8')};
`

const ItemLabel = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 700;
`

const Footer = styled.div`
  padding: 14px 16px calc(18px + env(safe-area-inset-bottom));
  border-top: 1px solid #e8edf4;
  background: #fff;
`

const FooterAction = styled.button`
  display: flex;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 11px;
  color: #61718a;
  font-size: 13px;
  font-weight: 700;
  background: #f4f7fa;
  cursor: pointer;
`

function PhoneNav({ callBack }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { Lang } = useContext(AppContext)
  const isDev = import.meta.env.DEV || false
  const [showMarquee, setShowMarquee] = useState(false)
  const [platform, setPlatform] = useState<'electron' | 'android' | 'browser'>('browser')
  const [hasNewVersion, setHasNewVersion] = useState(false)
  const navItems = [
    { label: Lang.Marketmanagement, path: '/marketbot', icon: LayoutDashboard },
    { label: Lang.Createwallets, path: '/createwallet', icon: WalletCards },
    { label: Lang.Sendtokens, path: '/sendertoken', icon: Send },
    { label: Lang.Batchcollection, path: '/collect', icon: Boxes },
  ]

  useEffect(() => { setPlatform(getPlatform()) }, [isDev])
  useEffect(() => {
    if (!isDev) checkUpdate(packageJson.version).then(setHasNewVersion)
  }, [isDev, platform])

  const navItemClick = (path: string) => {
    navigate({ pathname: path, search: location.search })
    callBack()
  }
  const isNative = platform === 'electron' || platform === 'android'
console.log('wwwwww::: ', 'wwwwww');
  return createPortal(
    <Overlay role="presentation" onClick={callBack}>
      <Drawer role="dialog" aria-modal="true" aria-label="Navigation" onClick={(event) => event.stopPropagation()}>
        <DrawerHead>
          <Brand>
            <BrandMark><Sparkles size={18} /></BrandMark>
          </Brand>
          <CloseButton type="button" onClick={callBack} aria-label="Close navigation"><X size={20} /></CloseButton>
        </DrawerHead>
        <Content>
          <SectionTitle>TOOLS</SectionTitle>
          <MenuGroup>
            {navItems.map(({ label, path, icon: Icon }) => {
              const active = location.pathname === path
              return <MenuItem key={path} type="button" $active={active} onClick={() => navItemClick(path)}>
                <IconBox $active={active}><Icon size={18} /></IconBox><ItemLabel>{label}</ItemLabel><ChevronRight size={18} opacity={0.45} />
              </MenuItem>
            })}
          </MenuGroup>
          <SectionTitle>EXPLORE</SectionTitle>
          <MenuGroup>
            <MenuItem as="a" href="https://sol.gtokentool.com/market" target="_blank" rel="noopener noreferrer">
              <IconBox><Sparkles size={18} /></IconBox><ItemLabel>{Lang.SOLBot || 'SOLBot'}</ItemLabel><ExternalLink size={17} opacity={0.45} />
            </MenuItem>
            <MenuItem as="a" href="https://oldrt.gtokentool.com" target="_blank" rel="noopener noreferrer">
              <IconBox><ExternalLink size={18} /></IconBox><ItemLabel>旧版本</ItemLabel><ExternalLink size={17} opacity={0.45} />
            </MenuItem>
          </MenuGroup>
          {isNative && hasNewVersion && showMarquee && <Marquee />}
        </Content>
        {isNative && <Footer>
          <FooterAction type="button" onClick={hasNewVersion ? () => setShowMarquee(!showMarquee) : () => window.location.reload()}>
            <RefreshCw size={17} />{hasNewVersion ? '升级新版本' : '刷新页面'}
          </FooterAction>
        </Footer>}
      </Drawer>
    </Overlay>,
    document.body,
  )
}

export default memo(PhoneNav)
